// src/components/tiptap/extensions/MetronomeNode.jsx
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiPlay, FiPause, FiMinus, FiPlus, FiMoreHorizontal, FiActivity } from 'react-icons/fi';

const LS_KEY_BPM = 'mms.metronome.bpm';
const LS_KEY_TS  = 'mms.metronome.ts';

function clamp(n, min, max) { return Math.min(max, Math.max(min, n)); }

function MetronomeView({ node, updateAttributes }) {
  const [bpm, setBpm] = useState(() => {
    const saved = Number(localStorage.getItem(LS_KEY_BPM));
    return Number.isFinite(saved) ? clamp(saved, 40, 240) : (Number(node.attrs.bpm) || 120);
  });
  const [timeSig, setTimeSig] = useState(() => {
    const saved = Number(localStorage.getItem(LS_KEY_TS));
    return [2,3,4,5,6].includes(saved) ? saved : (Number(node.attrs.timeSig) || 4);
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [beat, setBeat] = useState(0);
  const [showTsMenu, setShowTsMenu] = useState(false);

  const audioCtxRef     = useRef(null);
  const schedulerTidRef = useRef(null);
  const nextNoteTimeRef = useRef(0);
  const beatInBarRef    = useRef(0);

  const lookahead = 25;       // ms
  const scheduleAhead = 0.10; // s

 useEffect(() => {
   localStorage.setItem(LS_KEY_BPM, String(bpm));
 }, [bpm]);

 useEffect(() => {
   localStorage.setItem(LS_KEY_TS, String(timeSig));
 }, [timeSig]);

  const scheduleNote = useCallback((beatNumber, time) => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;

    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();

    const isDownbeat = (beatNumber % timeSig) === 0;
    osc.type = 'sine';
    osc.frequency.setValueAtTime(isDownbeat ? 1000 : 650, time);
    gain.gain.setValueAtTime(isDownbeat ? 0.35 : 0.25, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.05);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(time);
    osc.stop(time + 0.06);

    // Align visual pulse to audio clock
    const msUntil = (time - ctx.currentTime) * 1000;
    window.setTimeout(() => setBeat(beatNumber % timeSig), Math.max(0, msUntil));
  }, [timeSig]);

  const nextNote = useCallback(() => {
    const secondsPerBeat = 60 / bpm;
    nextNoteTimeRef.current += secondsPerBeat;
    beatInBarRef.current = (beatInBarRef.current + 1) % timeSig;
  }, [bpm, timeSig]);

  const scheduler = useCallback(() => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;

    while (nextNoteTimeRef.current < ctx.currentTime + scheduleAhead) {
        if (nextNoteTimeRef.current < ctx.currentTime - 0.5) {
        nextNoteTimeRef.current = ctx.currentTime;
        }
      scheduleNote(beatInBarRef.current, nextNoteTimeRef.current);
      nextNote();
    }
    schedulerTidRef.current = window.setTimeout(scheduler, lookahead);
  }, [nextNote, scheduleNote]);

  useEffect(() => {
    if (!isPlaying) {
      if (schedulerTidRef.current) clearTimeout(schedulerTidRef.current);
      setBeat(0);
      return;
    }
    if (!audioCtxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      audioCtxRef.current = new AC();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }

    beatInBarRef.current = 0;
    nextNoteTimeRef.current = audioCtxRef.current.currentTime + 0.10;
    scheduler();

    return () => {
      if (schedulerTidRef.current) clearTimeout(schedulerTidRef.current);
    };
  }, [isPlaying, bpm, timeSig, scheduler]);

  // Cleanup timer on unmount
  useEffect(() => () => {
    if (schedulerTidRef.current) clearTimeout(schedulerTidRef.current);
    if (audioCtxRef.current) {
   try { audioCtxRef.current.close(); } catch {}
   audioCtxRef.current = null;
 }
  }, []);

  const dots = useMemo(() => Array.from({ length: timeSig }), [timeSig]);

  return (
    <NodeViewWrapper
      as="div"
      contentEditable={false}
      role="group"
      tabIndex={-1}
      className="relative group w-full rounded-2xl bg-slate-50 shadow-sm p-4 border border-slate-200"
    >
      {/* Header — match Memory Match */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center">
            <FiActivity className="w-6 h-6" aria-hidden />
          </div>
          <div>
            <div className="text-lg font-bold text-slate-800">Metronome</div>
            <div className="text-sm text-slate-500">Lock in your rhythm with the metronome</div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
        {/* Play / Stop */}
        <button
          className={`h-10 w-10 rounded-full flex items-center justify-center text-white shadow transition
            ${isPlaying ? 'bg-orange-600 hover:brightness-95' : 'bg-orange-500 hover:bg-orange-600'}`}
          onClick={() => setIsPlaying(v => !v)}
          aria-label={isPlaying ? 'Stop metronome' : 'Start metronome'}
          title={isPlaying ? 'Stop' : 'Start'}
        >
          {isPlaying ? <FiPause /> : <FiPlay className="ml-0.5" />}
        </button>

        {/* Beat dots */}
        <div className="flex items-center gap-2 pr-1">
          {dots.map((_, i) => (
            <div
              key={i}
              className={`h-2.5 w-2.5 rounded-full transition-all
                ${isPlaying && beat === i
                  ? (i === 0
                      ? 'bg-orange-500 shadow ring-2 ring-orange-500/30 scale-110'
                      : 'bg-emerald-400 shadow ring-2 ring-emerald-400/30 scale-110')
                  : 'bg-white/70 border border-black/5'
                }`}
            />
          ))}
        </div>

        {/* – / BPM / + */}
        <div className="flex items-center gap-2">
          <button
            className="h-9 w-9 rounded-lg bg-white hover:bg-slate-100 border border-slate-300"
            onClick={() => setBpm(b => clamp(b - 1, 40, 240))}
            aria-label="Decrease BPM"
            title="−1"
          >
            <FiMinus className="mx-auto" />
          </button>
          <div className="h-9 rounded-lg bg-white/80 border border-orange-300 px-3 flex items-center font-semibold text-slate-800">
            {bpm}
          </div>
          <button
            className="h-9 w-9 rounded-lg bg-white hover:bg-slate-100 border border-slate-300"
            onClick={() => setBpm(b => clamp(b + 1, 40, 240))}
            aria-label="Increase BPM"
            title="+1"
          >
            <FiPlus className="mx-auto" />
          </button>
        </div>

        {/* Slider */}
        <input
          type="range"
          min={40}
          max={240}
          value={bpm}
          onChange={(e) => setBpm(clamp(parseInt(e.target.value, 10) || 120, 40, 240))}
          className="flex-1 h-2 rounded-lg appearance-none bg-black/10 cursor-pointer"
          style={{ accentColor: '#f97316' }} /* orange accent */
        />

        {/* Inline time signatures on ≥ md */}
        <div className="hidden md:flex items-center gap-2">
          {[2,3,4,5,6].map(ts => (
            <button
              key={ts}
              onClick={() => setTimeSig(ts)}
              className={`h-9 px-2 rounded-lg text-sm border transition
                ${timeSig === ts
                  ? 'bg-orange-500 text-white border-orange-500'
                  : 'bg-white hover:bg-slate-100 text-slate-800 border-slate-300'
                }`}
              aria-pressed={timeSig === ts}
              title={`${ts}/4`}
            >
              {ts}/4
            </button>
          ))}
        </div>

        {/* Ellipsis menu on < md */}
        <div className="relative md:hidden">
          <button
            onClick={() => setShowTsMenu(s => !s)}
            className="h-9 w-9 rounded-lg bg-white hover:bg-slate-100 border border-slate-300"
            aria-haspopup="menu"
            aria-expanded={showTsMenu}
            title="Time signature"
          >
            <FiMoreHorizontal className="mx-auto" />
          </button>
          {showTsMenu && (
            <div
              role="menu"
              className="absolute z-50 mt-2 w-44 rounded-lg border border-slate-300 bg-white shadow-lg p-2"
              onMouseDown={(e) => e.preventDefault()} // keep focus
            >
              <div className="text-xs font-medium text-black/60 px-1 pb-2">Time signature</div>
              <div className="grid grid-cols-3 gap-2">
                {[2,3,4,5,6].map(ts => (
                  <button
                    key={ts}
                    role="menuitemradio"
                    aria-checked={timeSig === ts}
                    onClick={() => { setTimeSig(ts); setShowTsMenu(false); }}
                    className={`py-1.5 rounded-md text-sm border transition
                      ${timeSig === ts
                        ? 'bg-orange-500 text-white border-orange-500'
                        : 'bg-white hover:bg-slate-100 text-slate-800 border-slate-300'
                      }`}
                  >
                    {ts}/4
                  </button>
                ))}
              </div>
              <div className="text-[11px] text-black/50 px-1 pt-2">Downbeat accented • tight timing</div>
            </div>
          )}
        </div>
      </div>
    </NodeViewWrapper>
  );
}

export const MetronomeNode = Node.create({
  name: 'metronomeTool',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      bpm: { default: 120 },
      timeSig: { default: 4 }, // 2–6
    };
  },
  parseHTML() { return [{ tag: 'metronome-tool' }]; },
  renderHTML({ HTMLAttributes }) { return ['metronome-tool', mergeAttributes(HTMLAttributes)]; },
  addNodeView() { return ReactNodeViewRenderer(MetronomeView); },
});

export default MetronomeNode;